<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { getStorageProxyUrl } from "@/util";
import { buildPresentationAnalyticsOriginState } from "../presentationAnalyticsNavigation";
import type { PresentationAnalyticsSelectorResult } from "./presentationAnalyticsSelector";

const props = defineProps<{
  result: PresentationAnalyticsSelectorResult;
  selectorEntryId: string;
}>();

const { t, locale } = useI18n();
const route = useRoute();

const viewCount = computed(() => Number(props.result.qualifiedViews) || 0);
const formattedViewCount = computed(() =>
  new Intl.NumberFormat(locale.value).format(viewCount.value),
);
const viewLabel = computed(() =>
  t(
    viewCount.value === 1
      ? "presentationAnalyticsSelector.result.view"
      : "presentationAnalyticsSelector.result.views",
    { count: formattedViewCount.value },
  ),
);
const detailRoute = computed(() => ({
  name: "PresentationAnalyticsDetail",
  params: {
    documentId: props.result.documentId,
  },
  state: buildPresentationAnalyticsOriginState(
    route.fullPath,
    props.selectorEntryId,
  ),
}));
</script>

<template>
  <router-link
    class="result_row"
    :to="detailRoute"
    :data-test="`presentation-analytics-result-${result.documentId}`"
  >
    <span class="result_position" aria-hidden="true">{{
      result.position
    }}</span>

    <span class="thumbnail">
      <img
        v-if="result.document?.thumbnailDownloadUrl"
        :src="getStorageProxyUrl(result.document.thumbnailDownloadUrl)"
        :alt="result.document.displayTitle"
      />
      <span v-else class="material-symbols-outlined" aria-hidden="true">
        description
      </span>
    </span>

    <span class="result_details">
      <strong>{{ result.document?.displayTitle }}</strong>
      <span class="workspace_path">
        {{ result.workspacePath.join(" › ") }}
      </span>
    </span>

    <span class="result_stat">
      {{ viewLabel }}
      <span aria-hidden="true"> · </span>
      {{ $t("presentationAnalyticsSelector.result.period") }}
    </span>

    <span class="material-symbols-outlined result_arrow" aria-hidden="true">
      chevron_right
    </span>
  </router-link>
</template>

<style scoped>
.result_row {
  display: grid;
  grid-template-columns: 2.25rem 4.5rem minmax(0, 1fr) auto 1.5rem;
  gap: 0.8rem;
  align-items: center;
  min-height: 5rem;
  padding: 0.65rem 0.8rem;
  border-bottom: 1px solid var(--classic-border-subtle, #ececec);
  color: inherit;
  text-decoration: none;
}

.result_row:hover,
.result_row:focus-visible {
  background: var(--classic-surface-hover, #f5f5f5);
}

.result_position {
  color: var(--classic-text-muted, #736c64);
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.thumbnail {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 3.75rem;
  overflow: hidden;
  border-radius: 0.3rem;
  background: var(--classic-media-surface, #fafafa);
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.thumbnail .material-symbols-outlined {
  color: var(--classic-text-muted, #888);
  font-size: 2rem;
}

.result_details {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.25rem;
}

.result_details strong,
.workspace_path {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace_path {
  color: var(--classic-text-muted, #736c64);
  font-size: 0.85rem;
}

.result_stat {
  color: var(--classic-text-secondary, #555);
  font-size: 0.85rem;
  white-space: nowrap;
}

.result_arrow {
  color: var(--classic-text-muted, #aaa);
}

@media (prefers-color-scheme: dark) {
  .result_row:focus-visible {
    outline: 2px solid var(--classic-focus);
    outline-offset: -2px;
  }
}

@media (max-width: 680px) {
  .result_row {
    grid-template-columns: 1.5rem 3.5rem minmax(0, 1fr) 1.25rem;
    gap: 0.6rem;
    padding: 0.7rem 0.25rem;
  }

  .thumbnail {
    width: 3.5rem;
    height: 3.2rem;
  }

  .result_stat {
    grid-column: 3;
    white-space: normal;
  }

  .result_arrow {
    grid-column: 4;
    grid-row: 1 / span 2;
  }
}
</style>
